import L from 'leaflet';
import GoogleMutant from 'leaflet.gridlayer.googlemutant';

/** Muted Google roadmap — less POI clutter, closer to fleet tracking UIs. */
export const FLEET_GOOGLE_MAP_STYLES = [
  { featureType: 'poi', stylers: [{ visibility: 'off' }] },
  { featureType: 'poi.business', stylers: [{ visibility: 'off' }] },
  { featureType: 'transit', stylers: [{ visibility: 'off' }] },
  { featureType: 'landscape', elementType: 'geometry', stylers: [{ color: '#f3f4f6' }] },
  { featureType: 'water', elementType: 'geometry', stylers: [{ color: '#dbeafe' }] },
  { featureType: 'road', elementType: 'geometry', stylers: [{ color: '#ffffff' }] },
  { featureType: 'road', elementType: 'geometry.stroke', stylers: [{ color: '#e5e7eb' }] },
] as const;

export function getGoogleMapsApiKey(): string {
  return (import.meta.env.VITE_GOOGLE_MAPS_API_KEY ?? '').trim();
}

let loadPromise: Promise<void> | null = null;

export function loadGoogleMapsScript(apiKey: string): Promise<void> {
  if (typeof window === 'undefined') {
    return Promise.reject(new Error('Google Maps is only available in the browser.'));
  }
  if (window.google?.maps) return Promise.resolve();

  if (loadPromise) return loadPromise;

  const existing = document.querySelector<HTMLScriptElement>('script[data-fleet-google-maps]');
  if (existing) {
    loadPromise = new Promise((resolve, reject) => {
      if (window.google?.maps) {
        resolve();
        return;
      }
      existing.addEventListener('load', () => resolve(), { once: true });
      existing.addEventListener('error', () => reject(new Error('Google Maps failed to load.')), {
        once: true,
      });
    });
    return loadPromise;
  }

  loadPromise = new Promise((resolve, reject) => {
    const script = document.createElement('script');
    script.src = `https://maps.googleapis.com/maps/api/js?key=${encodeURIComponent(apiKey)}`;
    script.async = true;
    script.defer = true;
    script.dataset.fleetGoogleMaps = 'true';
    script.onload = () => {
      if (window.google?.maps) resolve();
      else reject(new Error('Google Maps loaded but API is unavailable (check key and billing).'));
    };
    script.onerror = () => reject(new Error('Google Maps failed to load.'));
    document.head.appendChild(script);
  });

  return loadPromise;
}

export async function addGoogleFleetBasemap(
  map: L.Map,
): Promise<{ ok: true } | { ok: false; error: string }> {
  const apiKey = getGoogleMapsApiKey();
  if (!apiKey) {
    return { ok: false, error: 'No Google Maps API key configured.' };
  }

  try {
    await loadGoogleMapsScript(apiKey);
    if (!window.google?.maps) {
      return { ok: false, error: 'Google Maps script loaded but the API is unavailable.' };
    }

    new GoogleMutant({
      type: 'roadmap',
      maxZoom: 18,
      styles: [...FLEET_GOOGLE_MAP_STYLES],
    }).addTo(map);

    return { ok: true };
  } catch (err) {
    const message = err instanceof Error ? err.message : 'Google Maps failed to load.';
    return { ok: false, error: message };
  }
}
